import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import {
  generateFormData,
  getDiffObjs,
  handleAPIErrors,
  maxLenGenerator,
  minLenGenerator,
  renameFileData,
} from '../../utils';
import Head from '../../components/Head';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { Input, InputFile } from '../../components/Form';
import {
  organizationDemandCreate,
  organizationDemandGet,
  organizationDemandUpdate,
  organizationGet,
  organizationUpdate,
} from '../store/action/organization';
import Loader from '../../components/Loader';
import DarsupSweetAlert, { titleGenerator } from '../../components/SweetAlert';
import ImageFilePreview from '../../components/ImageFilePreview';
import Toast from '../../components/Toast';
import { NO_DIFF_TEXT, REQUIRED_TEXT_MESSAGE } from '../../utils/constants';
import { getAllPermissions } from '../store/action/PermissionAction';

export default function OrganizationForm({ history, match }) {
  const [sweetShow, setSweetShow] = useState(undefined);
  let [updated, setUpdated] = useState(false); // used to redirect in update

  let detailOfOrganizationDemand = useSelector(state => state.organization.demand);
  let detailOfOrganization = useSelector(state => state.organization.original);

  const { id } = match.params;
  let isDemand = !!new URLSearchParams(history.location.search).get('demand');
  let isDemandAccepted = isDemand & (detailOfOrganizationDemand.data?.status === 2);

  function conditionButton() {
    if (isDemand) {
      if (detailOfOrganizationDemand.data?.status === 2) return detailOfOrganizationDemand.data?.status === 2;
      return isDemand;
    }
    if (window.location.pathname === '/orgs/new') return true;
    return false;
  }

  let showEnglishName = conditionButton();
  const dispatch = useDispatch();

  const {
    handleSubmit,
    errors,
    control,
    setError,
    clearError,
    setValue,
    formState: { dirtyFields, dirty },
  } = useForm({ defaultValues: {}, mode: 'onChange' });

  let logoPreviewSrc;
  let isRenderedLogoPreview =
    id && (detailOfOrganizationDemand.data || detailOfOrganization.data) && !dirtyFields.has('logo_image');
  if (isRenderedLogoPreview)
    logoPreviewSrc = isDemand ? detailOfOrganizationDemand.data?.logo_image : detailOfOrganization.data?.logo_image;

  let bannerPreviewSrc;
  let isRenderedBannerPreview =
    id && (detailOfOrganizationDemand.data || detailOfOrganization.data) && !dirtyFields.has('banner_image');
  if (isRenderedBannerPreview)
    bannerPreviewSrc = isDemand
      ? detailOfOrganizationDemand.data?.banner_image
      : detailOfOrganization.data?.banner_image;

  let getErrMes = key => errors[key]?.message;

  function uploadFile(file, name) {
    let fileData = renameFileData(file, name);
    setValue(name, fileData);
  }

  let handleConfirm = () => setSweetShow(false);

  const onSubmit = async data => {
    let tempData = { ...data, english_name: data?.english_name?.split(' ')?.join('_') };
    let formData;

    if (id) {
      // separate update demand with original
      let originalData;
      if (isDemand)
        originalData = {
          ...detailOfOrganizationDemand.data,
          logo_image: undefined,
          banner_image: undefined,
        };
      else originalData = { ...detailOfOrganization.data, logo_image: undefined, banner_image: undefined };

      let dirtyFields = getDiffObjs(originalData, tempData); // used to update
      if (!dirtyFields) {
        // show toastify to no dirty fields
        Toast(NO_DIFF_TEXT, 'warning');
        return;
      }

      formData = generateFormData(dirtyFields);
      // separate update demand with original
      if (isDemand) dispatch(organizationDemandUpdate(id, formData));
      else dispatch(organizationUpdate(id, formData));

      setUpdated(true);
    } else {
      formData = generateFormData(tempData);
      dispatch(organizationDemandCreate(formData));
    }
  };

  useEffect(() => {
    if (id) {
      if (isDemand) dispatch(organizationDemandGet(id));
      else dispatch(organizationGet(id));
    }
  }, []);

  useEffect(() => {
    if (!id && detailOfOrganizationDemand.data) setSweetShow(true);

    // to update organization
    let condition = id && detailOfOrganizationDemand.data && !updated && isDemand;
    if (condition) {
      let { name, description, english_name } = detailOfOrganizationDemand.data;
      Object.entries({ name, description, english_name }).forEach(([key, value]) => {
        setValue(key, value);
      });
    }

    if (id && detailOfOrganizationDemand.data && updated) {
      dispatch(getAllPermissions());
      history.push('/');
    }
  }, [detailOfOrganizationDemand.data]);

  useEffect(() => {
    // to update organization
    let condition = id && detailOfOrganization.data && !updated && !isDemand;
    if (condition) {
      let { name, description, english_name } = detailOfOrganization.data;
      Object.entries({ name, description, english_name }).forEach(([key, value]) => {
        setValue(key, value);
      });
    }

    if (id && detailOfOrganization.data && updated) {
      dispatch(getAllPermissions());
      history.push('/orgs');
    }
  }, [detailOfOrganization.data]);

  useEffect(() => {
    if (detailOfOrganizationDemand.error) {
      handleAPIErrors(detailOfOrganizationDemand.error, (name, message) => {
        setError(name, 'notMatch', message);
      });
    }
  }, [detailOfOrganizationDemand.error]);

  useEffect(() => {
    if (detailOfOrganization.error) {
      handleAPIErrors(detailOfOrganization.error, (name, message) => {
        setError(name, 'notMatch', message);
      });
    }
  }, [detailOfOrganization.error]);

  useEffect(() => {
    if (!sweetShow && sweetShow !== undefined) history.push('/');
  }, [sweetShow]);

  return (
    <>
      <Head title={'ایجاد سازمان'} />
      <div className="kt-portlet p-3">
        <div className="kt-portlet__body">
          <Container>
            <Row>
              <Col md={{ span: 8, offset: 2 }}>
                <Form onSubmit={handleSubmit(onSubmit)}>
                  <Row className={'mb-4'}>
                    <Col xs={12} lg={showEnglishName ? 6 : 12}>
                      <Input
                        type={'text'}
                        name={'name'}
                        control={control}
                        error={getErrMes('name')}
                        label={'نام سازمان'}
                        placeholder={'درس‌آپ'}
                        rules={{
                          required: REQUIRED_TEXT_MESSAGE,
                          minLength: { value: 3, message: minLenGenerator(3) },
                        }}
                      />
                    </Col>
                    {showEnglishName && (
                      <Col xs={12} lg={6}>
                        <Input
                          type={'text'}
                          name={'english_name'}
                          control={control}
                          error={getErrMes('english_name')}
                          info={'نام سازمان خود را به اختصار وارد کنید مانند darsup(کمتر از 20 کاراکتر باید باشد)'}
                          label={'نام انگلیسی سازمان'}
                          placeholder={'Darsup'}
                          dir={'ltr'}
                          rules={{
                            required: REQUIRED_TEXT_MESSAGE,
                            minLength: { value: 2, message: minLenGenerator(2) },
                            maxLength: { value: 20, message: maxLenGenerator(20) },
                            pattern: {
                              value: /^[a-z0-9A-Z_ ]+$/,
                              message: 'لطفا مقدار صحیح وارد کنید',
                            },
                          }}
                        />
                      </Col>
                    )}
                  </Row>
                  <Row className={'mb-4'}>
                    <Col xs={12}>
                      <Input
                        type={'textarea'}
                        name={'description'}
                        control={control}
                        error={getErrMes('description')}
                        label={'معرفی سازمان'}
                        placeholder={'معرفی سازمان'}
                        rows={'4'}
                        maxLength={'800'}
                        rules={{
                          required: REQUIRED_TEXT_MESSAGE,
                          minLength: { value: 10, message: minLenGenerator(10) },
                        }}
                      />
                    </Col>
                  </Row>
                  <Row className={'mb-4'}>
                    <Col xs={12}>
                      <InputFile
                        name={'logo_image'}
                        control={control}
                        fileType={'image'}
                        limitSize={3}
                        label={'لوگو سازمان'}
                        setError={setError}
                        clearError={clearError}
                        errors={errors}
                        error={getErrMes('logo_image')}
                        rules={{ required: !id ? REQUIRED_TEXT_MESSAGE : false }}
                        onUpload={uploadFile}
                      />
                      {isRenderedLogoPreview && <ImageFilePreview src={logoPreviewSrc} caption={'تصویر آپلود شده'} />}
                    </Col>
                  </Row>
                  <Row className={'mb-4'}>
                    <Col xs={12}>
                      <InputFile
                        name={'banner_image'}
                        control={control}
                        fileType={'image'}
                        limitSize={3}
                        label={'تصویر بنر'}
                        setError={setError}
                        clearError={clearError}
                        errors={errors}
                        minWidth={1920}
                        minHeight={714}
                        error={getErrMes('banner_image')}
                        onUpload={uploadFile}
                        imageHeight={130}
                        imageWidth={350}
                      />
                      {isRenderedBannerPreview && bannerPreviewSrc && (
                        <ImageFilePreview src={bannerPreviewSrc} caption={'تصویر آپلود شده'} />
                      )}
                    </Col>
                  </Row>
                  {!isDemandAccepted && (
                    <Row>
                      <Col xs={12}>
                        <Button type="submit" variant="primary" className="btn-block my-4 font-weight-bold">
                          {id ? 'ویرایش سازمان' : 'ساخت سازمان'}
                        </Button>
                      </Col>
                    </Row>
                  )}
                </Form>
              </Col>
            </Row>
            {/*{!demandParam && id && <Details/>}*/}
          </Container>
        </div>
      </div>
      <Loader visible={detailOfOrganizationDemand.isLoading || detailOfOrganization.isLoading} />
      <DarsupSweetAlert type={'success'} title={titleGenerator('org')} show={!!sweetShow} onConfirm={handleConfirm}>
        درخواست ساخت سازمان شما در سامانه درس آپ ثبت شد. نتیجه درخواست تا نهایتا دو ساعت برای شما ارسال خواهد شد.
      </DarsupSweetAlert>
    </>
  );
}
